import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  KeyCell,
  KeyValueRow,
  KeyValueTable,
  ValueCell,
} from '@amzn/storm-ui';
import { useQuery } from '@tanstack/react-query';
import { ErrorDisplay } from '../../../../../Shared/components/ErrorDisplay/ErrorDisplay';
import { ApiError } from '../../../../../Shared/interfaces/ApiError';
import useApiClient from '../../../../../Shared/services/api';
import { CustomCard } from '../../../../../UiComponentsLibrary/CustomCard';
import CustomSpinner from '../../../../../UiComponentsLibrary/CustomSpinner';
import { CustomButton } from '../../../../../UiComponentsLibrary/CustomButton';
import { CustomButtonColor } from '../../../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import { getBookingDetailsById } from '../../../../services/service';
import { transformBookingWithSlots } from './BookingViewHelpers';
import BookingSlotDetailsSection from './BookingSlotDetailsSection';

interface BookingViewProps {
  bookingId: string;
  isViewBooking: boolean;
  setIsViewBooking: React.Dispatch<React.SetStateAction<boolean>>;
}

const BookingView: React.FC<BookingViewProps> = ({
  bookingId,
  isViewBooking,
  setIsViewBooking,
}) => {
  const { t } = useTranslation();
  const api = useApiClient();

  const {
    error,
    isLoading,
    isError,
    data: bookingData,
  } = useQuery({
    queryKey: ['booking-details', bookingId],
    queryFn: () => getBookingDetailsById(api, bookingId),
    enabled: !!bookingId && isViewBooking,
  });

  const bookingWithSlots = bookingData?.booking
    ? transformBookingWithSlots(bookingData.booking, t)
    : null;

  const errorDisplayProps = {
    error: (error || {
      errorCode: 'unknown-error',
      statusCode: 500,
      message: t('view-user-error'),
    }) as ApiError,
    customMessage: !error ? t('view-user-error') : undefined,
  };

  return (
    <CustomCard title={t('view-booking-label')}>
      <Flex direction="column" rowGap="small">
        {isLoading ? (
          <div id="view-booking-spinner">
            <CustomSpinner />
          </div>
        ) : isError || !bookingWithSlots ? (
          <div id="view-booking-api-error">
            <ErrorDisplay {...errorDisplayProps} />
          </div>
        ) : (
          <>
            <KeyValueTable
              id="view-booking-table"
              caption={t('view-booking-table-caption')}
            >
              {Object.entries(bookingWithSlots.tableData).map(
                ([key, value]) => (
                  <KeyValueRow id={`view-booking-row-${key}`} key={key}>
                    <KeyCell id={`view-booking-key-${key}`}>{t(key)}</KeyCell>
                    <ValueCell id={`view-booking-value-${key}`}>
                      {value === null || value === undefined
                        ? '-'
                        : String(value)}
                    </ValueCell>
                  </KeyValueRow>
                )
              )}
            </KeyValueTable>

            {/*contextual slot section */}
            {bookingWithSlots.contextualSlotDetails.length > 0 && (
              <BookingSlotDetailsSection
                slots={bookingWithSlots.contextualSlotDetails}
                isContextual={true}
              />
            )}

            {/*noncontextual slot section*/}
            {bookingWithSlots.nonContextualSlotDetails.length > 0 && (
              <BookingSlotDetailsSection
                slots={bookingWithSlots.nonContextualSlotDetails}
                isContextual={false}
              />
            )}
          </>
        )}
        <CustomButton
          id="view-booking-back-button"
          text={t('view-booking-back-button')}
          backgroundColor={CustomButtonColor.cancel}
          handleOnClickCb={() => setIsViewBooking(false)}
        />
      </Flex>
    </CustomCard>
  );
};

export default BookingView;
